<template>
  <!-- Table Container Card -->
  <b-card no-body>
    <div
      v-if="individualCustomerAccountSuccessCount || individualCustomerAccountFailed.length"
      class="m-2"
    >
      <b-row>
        <b-col
          v-if="individualCustomerAccountSuccessCount"
          md="6"
          class="mb-2"
        >
          <b-alert
            show
            variant="primary"
            class="p-2"
          >
            {{ individualCustomerAccountSuccessCount }}
          </b-alert>
        </b-col>
        <b-col
          v-if="individualCustomerAccountFailed.length"
          md="6"
          class="mb-2"
        >
          <div>
            <b-button variant="danger">
              {{ $t('FAILED LISTS') }}
            </b-button>
          </div>
          <b-table
            ref="individualCustomerAccountFailedReport"
            :items="individualCustomerAccountFailed"
            responsive
            :fields="translatedFailedHeaders"
            primary-key="id"
            show-empty
            :empty-text="$t('No records available')"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            class="position-relative"
          /></b-col>
      </b-row>
    </div>

    <div class="m-2">
      <b-row class="d-flex align-items-center">
        <b-col md="6">
          <validation-observer ref="individualCustomerAccountRules">
            <b-form>
              <b-col
                md="12"
                class="mb-2"
              >
                <div class="mb-1">
                  {{ $t('Select file (.xlsx format only)') }}
                </div>
                <b-form-file
                  id="file-name"
                  v-model="hasFileData"
                  :label="$t('Select file (.xlsx format only)')"
                  :state="Boolean(hasFileData)"
                  :placeholder="$t('Choose a file or drop it here...')"
                  :drop-placeholder="$t('Drop file here...')"
                  error-name="file"
                  rules="required"
                  accept=".xlsx"
                  @change="onChangeXLSX($event)"
                />
              </b-col>
            </b-form>
          </validation-observer>
        </b-col>

        <b-col
          md="6"
        >
          <b-row>
            <b-col md="5">
              <Button
                v-if="getUserPermission('DATA_MIGRATION') && parseExcelFile.length"
                :label="$t('Next >')"
                size="md"
                :hidden="isNextValidateHidden"
                :loading="isLoading"
                @click="handleNextValidate"
              />
              <Button
                v-if="getUserPermission('DATA_MIGRATION') && !errorMessages.length"
                :label="$t('Create Individual Customer Accounts')"
                size="md"
                :hidden="isCreateBatchHidden"
                :loading="isLoading"
                @click="handleCreate"
              />
            </b-col>
            <b-col md="7">
              <Button
                class="ml-lg-2"
                align="end"
                :label="$t('Download Individual Customer Account Format')"
                size="md"
                icon="DownloadIcon"
                show-icon
                :loading="isLoading2"
                :disabled="isDisabled2"
                @click="handleDownloadSubmit"
              />
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </div>
    <b-table
      v-if="errorMessages.length"
      ref="individualCustomerAccountSuccessCountReport"
      :items="errorMessages"
      responsive
      :fields="errorHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    />
    <p
      v-if="totalSheetRows"
      class="p-2"
      style="font-size: 18px;"
    >
      {{ $t('There are total of') }} ({{ totalSheetRows }}) {{ $t('Records fetched') }}
    </p>
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BForm, BTable, BFormFile, BButton, BAlert,
} from 'bootstrap-vue'
import { required } from '@validations'
import { ValidationObserver } from 'vee-validate'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
// import Papa from 'papaparse'
import XLSX from 'xlsx'
import Button from '../../../components/button/Button.vue'
import { batchAccountFormat, tableLessHeaders, tableAllHeaders } from '../../../../helpers/data'
import { toYearWithDate } from '../../../../helpers/formatDate'

export default {
  components: {
    BCard,
    BTable,
    BRow,
    BCol,
    BForm,
    BFormFile,
    ValidationObserver,
    Button,
    BButton,
    BAlert,
  },
  filters: {
    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      fileData: [],
      hasFileData: [],
      parseExcelFile: [],
      validatedExcelFileData: [],
      totalSheetRows: '',
      sortOrders: {},
      accountFormat: batchAccountFormat,
      successHeaders: tableAllHeaders,
      failedHeaders: tableLessHeaders,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'oldCustomerID', sortable: true },
        { key: 'customerType', sortable: true },
        { key: 'title', sortable: true },
        { key: 'lastName', sortable: true },
        { key: 'firstName', sortable: true },
        { key: 'otherNames', sortable: true },
        { key: 'gender', sortable: true },
        { key: 'address', sortable: true },
        { key: 'maritalStatus', sortable: true },
        { key: 'maidenName', sortable: true },
        { key: 'religion', sortable: true },
        { key: 'signatory' },
        { key: 'mandateRule' },
        { key: 'phoneNumber', sortable: true },
        { key: 'email', sortable: true },
        { key: 'hometown', sortable: true },
        { key: 'dateOfBirth', sortable: true },
        { key: 'nationality', sortable: true },
        { key: 'state', sortable: true },
        { key: 'lga', sortable: true },
        { key: 'meansOfIdentification', sortable: true },
        { key: 'meansOfIdentificationNumber', sortable: true },
        { key: 'nokName', sortable: true },
        { key: 'nokAddress', sortable: true },
        { key: 'nokGender', sortable: true },
        { key: 'nokRelationship', sortable: true },
        { key: 'nokPhoneNumber', sortable: true },
        { key: 'nokEmail', sortable: true },
        { key: 'occupation', sortable: true },
        { key: 'employerName', sortable: true },
        { key: 'employerAddress', sortable: true },
        { key: 'employerPhoneNumber', sortable: true },
        { key: 'referenceAccountNumber', sortable: true },
        { key: 'referenceNUBAN', sortable: true },
        { key: 'bvn', sortable: true },
        { key: 'accountName', sortable: true },
        { key: 'accountType', sortable: true },
        { key: 'accountStatus', sortable: true },
        { key: 'branchCode', sortable: true },
        { key: 'productCode', sortable: true },
        { key: 'accountOfficerStaffID', sortable: true },
        { key: 'accountTierLevel', sortable: true },
        { key: 'accessLevel', sortable: true },
        { key: 'enableEmailNotification', sortable: true },
        { key: 'enableSMSNotification', sortable: true },
        { key: 'statementDeliveryMode', sortable: true },
        { key: 'statementDeliveryFrequency', sortable: true },
        { key: 'minimumBalanceRequired', sortable: true },
        { key: 'referee1CustomerID', sortable: true },
        { key: 'referee2CustomerID', sortable: true },
      ],
      required,
      isDisabled: false,
      isLoading: false,
      isDisabled2: false,
      isLoading2: false,
      showFormError: false,
      isCreateBatchHidden: true,
      isNextValidateHidden: false,
      individualCustomerAccountSuccessCount: '',
      individualCustomerAccountFailed: [],
      sortDesc: false,
      sortBy: 'lastName',
      errorHeaders: [
        { key: 'referenceNumber', sortable: true, label: 'Reference Number' },
        { key: 'message', sortable: true, label: 'Error Messages' },
      ],
      errorMessages: [],
    }
  },
  computed: {
    translatedFailedHeaders() {
      return this.failedHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  methods: {
    ...mapActions({
      batchAccountCreation: 'batch/batchAccountCreation',
      downloadAccountFormat: 'batch/downloadAccountFormat',
      validateBatchAccountCreation: 'batch/validateBatchAccountCreation',
    }),
    async handleCreate() {
      this.$refs.individualCustomerAccountRules.validate().then(success => {
        if (success) {
          this.confirm(this.batchAccountCreationAction, { text: this.$t('Are you sure you want to Create Individual Customer Accounts?') })
        }
      })
    },
    async batchAccountCreationAction() {
      try {
        this.individualCustomerAccountSuccessCount = ''
        this.showFormError = false
        this.isDisabled = true
        this.isLoading = true
        const formData = this.parseExcelFile.map(item => ({
          ...item,
          // customerType: item.customerType ? Number(item.customerType) : '',
          gender: item.gender ? Number(item.gender) : null,
          maritalStatus: item.maritalStatus ? Number(item.maritalStatus) : null,
          email: item.email ? item.email : null,
          phoneNumber: item.phoneNumber ? item.phoneNumber : null,
          meansOfIdentificationNumber: item.meansOfIdentificationNumber ? item.meansOfIdentificationNumber : null,
          religion: item.religion ? Number(item.religion) : null,
          dateOfBirth: item.dateOfBirth ? toYearWithDate(item.dateOfBirth) : null,
          meansOfIdentification: item.meansOfIdentification ? Number(item.meansOfIdentification) : null,
          nokGender: item.nokGender ? Number(item.nokGender) : null,
          accountType: item.accountType ? Number(item.accountType) : null,
          accountStatus: item.accountStatus ? Number(item.accountStatus) : null,
          accountTierLevel: item.accountTierLevel ? Number(item.accountTierLevel) : 1,
          accessLevel: item.accessLevel ? Number(item.accessLevel) : 1,
          enableEmailNotification: item.enableEmailNotification ? Boolean(item.enableEmailNotification) : false,
          enableSMSNotification: item.enableSMSNotification ? Boolean(item.enableSMSNotification) : false,
          statementDeliveryMode: item.statementDeliveryMode ? Number(item.statementDeliveryMode) : null,
          statementDeliveryFrequency: item.statementDeliveryFrequency ? Number(item.statementDeliveryFrequency) : null,
          minimumBalanceRequired: item.minimumBalanceRequired ? Number(item.minimumBalanceRequired) : null,
        }))
        const response = await this.batchAccountCreation({ payload: formData })
        const { status, data } = response
        if (status === 200) {
          this.hasFileData = []
          this.parseExcelFile = []
          if (data.successCount > 1) {
            const pluralText = this.$t('Individual Customer Accounts was successfully created')
            this.individualCustomerAccountSuccessCount = `(${data.successCount}) ${pluralText}`
          } else {
            const singularText = this.$t('Individual Customer Account was successfully created')
            this.individualCustomerAccountSuccessCount = `(${data.successCount}) ${singularText}`
          }
          this.individualCustomerAccountFailed = data.failed

          this.isDisabled = false
          this.isLoading = false
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const reasonText = this.$t('Your request to create Individual Customer Accounts failed. Reason')
          this.alertError({ text: `${reasonText}: ${responseError.errorMessage}` })
        })
        this.showFormError = true
      } finally {
        this.isDisabled = false
        this.isLoading = false
      }
    },
    onChangeXLSX(event) {
      this.errorMessages = []
      this.isCreateBatchHidden = true
      this.fileData = event.target.files ? event.target.files[0] : null
      if (this.fileData) {
        const vm = this
        const reader = new FileReader()
        reader.onload = e => {
          const data = e.target.result
          const workbook = XLSX.read(data, { type: 'binary', cellDates: true, dateNF: 'mm/dd/yyyy;@' })
          const sheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[sheetName]
          const rowObject = XLSX.utils.sheet_to_row_object_array(worksheet, { raw: false })
          vm.totalSheetRows = rowObject.length
          vm.parseExcelFile = JSON.parse(JSON.stringify(rowObject)).map(items => ({
            oldCustomerID: items['Old Customer ID'],
            customerType: items['Customer Type'],
            title: items['Title '],
            lastName: items['Last Name'],
            firstName: items['First Name'],
            otherNames: items['Other Names'],
            gender: items['Gender '],
            address: items['Address '],
            maritalStatus: items['Marital Status'],
            maidenName: items['Maiden Name'],
            religion: items['Religion '],
            signatory: items['Signatory '],
            mandateRule: items['Mandate Rule'],
            phoneNumber: items['Phone Number'],
            email: items['Email '],
            hometown: items['Home Town'],
            dateOfBirth: items['Date Of Birth'],
            nationality: items['Nationality '],
            state: items['State '],
            lga: items['LGA '],
            meansOfIdentification: items['Means Of Identification'],
            meansOfIdentificationNumber: items['Means Of Identification Number'],
            nokName: items['Next Of Kin Name'],
            nokAddress: items['Next Of Kin Address'],
            nokGender: items['Next Of Kin Gender'],
            nokRelationship: items['Next Of Kin Relationship'],
            nokPhoneNumber: items['Next Of Kin Phone Number'],
            nokEmail: items['Next Of Kin Email'],
            occupation: items['Occupation '],
            employerName: items['Employer Name'],
            employerAddress: items['Employer Address'],
            employerPhoneNumber: items['Employer Phone Number'],
            referenceAccountNumber: items['Reference Account Number'],
            referenceNUBAN: items['Reference NUBAN'],
            bvn: items['Bank Verification Number'],
            accountName: items['Account Name'],
            accountType: items['Account Type'],
            accountStatus: items['Account Status'],
            branchCode: items['Branch Code'],
            productCode: items['Product Code'],
            accountOfficerStaffID: items['Account Officer Staff ID'],
            accountTierLevel: items['Account Tier Level'],
            accessLevel: items['Access Level'],
            enableEmailNotification: items['Enable Email Notification'],
            enableSMSNotification: items['Enable SMS Notification'],
            statementDeliveryMode: items['Statement Delivery Mode'],
            statementDeliveryFrequency: items['Statement Delivery Frequency'],
            minimumBalanceRequired: items['Minimum Balance Required'],
            referee1CustomerID: items['Referee1 Customer ID'],
            referee2CustomerID: items['Referee2 Customer ID'],
          }))
        }
        reader.readAsBinaryString(this.fileData)
      }
    },
    async handleNextValidate() {
      try {
        this.isLoading = true
        this.hasFileData = []
        const formData = this.parseExcelFile.map(item => ({
          ...item,
          customerType: item.customerType ? Number(item.customerType) : null,
          gender: item.gender ? Number(item.gender) : null,
          email: item.email ? item.email : null,
          phoneNumber: item.phoneNumber ? item.phoneNumber : null,
          meansOfIdentificationNumber: item.meansOfIdentificationNumber ? item.meansOfIdentificationNumber : null,
          maritalStatus: item.maritalStatus ? Number(item.maritalStatus) : null,
          religion: item.religion ? Number(item.religion) : null,
          dateOfBirth: item.dateOfBirth ? toYearWithDate(item.dateOfBirth) : null,
          meansOfIdentification: item.meansOfIdentification ? Number(item.meansOfIdentification) : null,
          nokGender: item.nokGender ? Number(item.nokGender) : null,
          accountType: item.accountType ? Number(item.accountType) : null,
          accountStatus: item.accountStatus ? Number(item.accountStatus) : null,
          accountTierLevel: item.accountTierLevel ? Number(item.accountTierLevel) : 1,
          accessLevel: item.accessLevel ? Number(item.accessLevel) : 1,
          enableEmailNotification: item.enableEmailNotification ? Boolean(item.enableEmailNotification) : false,
          enableSMSNotification: item.enableSMSNotification ? Boolean(item.enableSMSNotification) : false,
          statementDeliveryMode: item.statementDeliveryMode ? Number(item.statementDeliveryMode) : null,
          statementDeliveryFrequency: item.statementDeliveryFrequency ? Number(item.statementDeliveryFrequency) : null,
          minimumBalanceRequired: item.minimumBalanceRequired ? Number(item.minimumBalanceRequired) : null,
        }))
        const response = await this.validateBatchAccountCreation({ payload: formData })
        const { status, data } = response
        if (status === 200) {
          this.errorMessages = data.failed
          if (data.successCount > 1) {
            const plural = this.$t('Individual Customer Accounts are ready to be created')
            this.individualCustomerAccountSuccessCount = `(${data.successCount}) ${plural}`
          } else {
            const singular = this.$t('Individual Customer Account is ready to be created')
            this.individualCustomerAccountSuccessCount = `(${data.successCount}) ${singular}`
          }
          this.isCreateBatchHidden = false
          if (this.errorMessages.length) {
            this.isNextValidateHidden = false
          } else {
            this.isNextValidateHidden = true
            this.alertSuccess({ text: this.$t('Everything seems good! Continue with Individual Customer Creation') })
          }
          this.isLoading = false
        }
      } catch (error) {
        const errorText = this.$t('Your request to perform Individual Customer Account validation failed. Reason')
        error.response.data.errors.forEach(responseError => {
          this.alertError({
            text: `${errorText}: ${responseError.errorMessage}`,
          })
        })
        this.isCreateBatchHidden = true
        this.isLoading = false
      }
    },
    async handleDownloadSubmit() {
      this.confirm(this.downloadAction, { text: this.$t('Are you sure you want to download Individual Customer Creation Format?') })
    },
    async downloadAction() {
      try {
        this.isLoading2 = true
        this.isDisabled2 = true
        const INDIVIDUAL_CUSTOMER_ACCOUNT_CREATION_TEMPLATE = 15
        const response = await this.downloadAccountFormat(INDIVIDUAL_CUSTOMER_ACCOUNT_CREATION_TEMPLATE)
        if (response.status === 200) {
          const downloadAnchor = document.createElement('a')
          downloadAnchor.setAttribute('href', response.data.data)
          const filename = 'Udara360-Individual-Account-Creation.xlsx'
          downloadAnchor.setAttribute('download', filename)
          document.body.appendChild(downloadAnchor)
          downloadAnchor.click()
          document.body.removeChild(downloadAnchor)
          this.alertSuccess({ text: this.$t('Your request to download Individual Customer Creation Format was successful') })
        }
      } catch (error) {
        const reasonErrorText = this.$t('Your request to download Individual Customer Creation Format failed. Reason')
        error.response.data.errors.forEach(responseError => {
          this.alertError({ text: `${reasonErrorText}: ${responseError.errorMessage}` })
          this.isLoading2 = false
          this.isDisabled2 = false
        })
      } finally {
        this.isLoading2 = false
        this.isDisabled2 = false
      }
    },
  },
}
</script>
